import type { FC } from "react";
import Select from "react-select";
import { useThemeMode } from "flowbite-react";
import { HiOfficeBuilding } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";
import { useGetAllCompaniesQuery } from "@/api/companyApi/company";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";

const SuperAdminCompanySelector: FC = () => {
    const { t } = useTranslation(["company"]);
    const { mode } = useThemeMode();
    const user = useCurrentUser();
    const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
    const { selectedCompany, setSelectedCompany } = useSelectedCompany();

    // Must pass page:1 — the backend computes offset = (page-1)*pageSize, so a
    // missing page becomes 0 → offset -pageSize → empty result.
    const { data } = useGetAllCompaniesQuery(
        { page: 1, pageSize: 200 },
        { skip: !isSuperAdmin }
    );

    if (!isSuperAdmin) return null;

    const options = (data?.items ?? []).map((c) => ({
        label: c.company_name,
        value: String(c.company_id),
    }));

    const currentValue = selectedCompany
        ? { label: selectedCompany.name, value: String(selectedCompany.id) }
        : null;

    const isDark = mode === "dark";

    return (
        <div className="flex items-center gap-2">
            <HiOfficeBuilding className="hidden sm:block w-5 h-5 text-indigo-500" />
            <Select
                classNamePrefix="company-select"
                options={options}
                value={currentValue}
                placeholder={t("selectCompany")}
                isClearable
                onChange={(opt) =>
                    setSelectedCompany(
                        opt ? { id: Number(opt.value), name: opt.label } : null
                    )
                }
                styles={{
                    container: (base) => ({ ...base, minWidth: 200 }),
                    control: (base) => ({
                        ...base,
                        backgroundColor: isDark ? "#374151" : "#fff",
                        borderColor: isDark ? "#4b5563" : "#d1d5db",
                        minHeight: 38,
                    }),
                    singleValue: (base) => ({
                        ...base,
                        color: isDark ? "#f9fafb" : "#111827",
                    }),
                    input: (base) => ({
                        ...base,
                        color: isDark ? "#f9fafb" : "#111827",
                    }),
                    menu: (base) => ({
                        ...base,
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        zIndex: 60,
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                            ? isDark
                                ? "#374151"
                                : "#eef2ff"
                            : "transparent",
                        color: isDark ? "#f9fafb" : "#111827",
                    }),
                }}
            />
        </div>
    );
};

export default SuperAdminCompanySelector;
